import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useMemo } from 'react';
import { capitalizeFirstLetter } from '../../../../utils/texts';
import { RHFCheckbox, RHFRadioButton } from '../../../components/rhf';
import { classNames } from 'primereact/utils';
import { HlasovaciMoznost } from '../../../constants/enums';

export const MemberVoteTable = ({
    members,
    memberVoteEditLogs,
    voteOptions,
    voteOptionLabelsMap,
    control,
    onSetVoteOption,
    onVoteOptionChanged,
    onClickMemberVoteEditHistory 
}) => {
    const getVoteOptionLabel = (option) => capitalizeFirstLetter(voteOptionLabelsMap?.[option.id].label ?? option.code);

    const voteOptionAbbreviations = useMemo(
        () =>
            voteOptions.reduce((acc, option) => {
                let abbreviation = option.code[0];
                if (acc.find((a) => a === abbreviation)) {
                    abbreviation += option.code.slice(1, 3);
                }
                acc.push(abbreviation);
                return acc;
            }, []),
        [voteOptions]
    );

    const orderedMembers = useMemo(() => {
        return members.toSorted((a, b) => a.order - b.order);
    }, [members]);

    const voteOptionTitles = useMemo(() => {
        const map = new Map();
        voteOptions.forEach((option) => {
            map.set(option.id, getVoteOptionLabel(option));
        });
        return map;
    }, [voteOptions]);

    const renderHistoryColumn = useMemo(() => {
        return members.some((member) => 
            Object.keys(memberVoteEditLogs[member.id] ?? {}).length > 0
        );
    }, [members, memberVoteEditLogs]);

    const voteOptionTemplate = (rowData, optionId) => {
        return (
            <RHFRadioButton
                control={control}
                name={`members[${rowData.id}].option`}
                key={`members[${rowData.id}].option`}
                rules={{ required: true }}
                inputProps={{
                    value: optionId,
                    title: voteOptionTitles.get(optionId)
                }}
                onChange={(e, field) => {
                field.onChange(e);
                onVoteOptionChanged();
            }}
            />
        );
    };

    const inPersonTemplate = (rowData) => {
        return (
            <RHFCheckbox
                control={control}
                name={`members[${rowData.id}].in_person`}
                key={`members[${rowData.id}].in_person`}
            />
        );
    };

    const editHistoryTemplate = (rowData) => {
        return (
            memberVoteEditLogs[rowData.id] && Object.keys(memberVoteEditLogs[rowData.id]).length > 0 ? (
                <span
                className='pi pi-book member-vote-edit-icon'
                onClick={() => onClickMemberVoteEditHistory({
                    first_name: rowData.first_name,
                    last_name: rowData.last_name,
                    edit_history: memberVoteEditLogs[rowData.id]
                })}
                title="Historie úprav"
                />
            ) : null
        );
    };

    return (
        <DataTable
            value={orderedMembers}
            removableSort
            sortMode='multiple'
            stripedRows
            breakpoint='1px'
            size='small'
            emptyMessage='Žádní členové nebyli nalezeni'
            className='member-vote-table'
        >
            <Column field='first_name' header='Jméno' bodyClassName='break-word' sortable />
            <Column field='last_name' header='Příjmení' bodyClassName='break-word' sortable />
            <Column field='political_entity.abbreviation' header='Subjekt' sortable />
            {voteOptions.map((option, idx, arr) => {
                const isAbsentOrExcused =
                    option.id === HlasovaciMoznost.ABSENT ||
                    option.id === HlasovaciMoznost.EXCUSED;
                return (
                    <Column
                        header={
                            <span
                                title={`${getVoteOptionLabel(option)}.${
                                    !isAbsentOrExcused
                                        ? ' Kliknutím nastavíte hlasovací možnost pro všechny členy (ponechá hlasovací možnost nepřítomen a omluven).'
                                        : ''
                                }`}
                                className={classNames({
                                    'clickable-text': !isAbsentOrExcused
                                })}
                                onClick={
                                    !isAbsentOrExcused
                                        ? () => onSetVoteOption(option.id)
                                        : undefined
                                }
                            >
                                <span className='header-text--long'>{getVoteOptionLabel(option)}</span>
                                <span className='header-text--short'>{voteOptionAbbreviations[idx]}</span>
                            </span>
                        }
                        field={`option-${option.id}`}
                        key={`option-${option.id}`}
                        body={(rowData) => voteOptionTemplate(rowData, option.id)}
                        align='center'
                        className={classNames({
                            'table-cell-border-left': idx === 0,
                            'table-cell-border-right': idx === arr.length - 1
                        })}
                    />
                );
            })}
            <Column
                field='in_person'
                header={
                    <>
                        <span className='header-text--long'>Prezenčně</span>
                        <span className='header-text--short'>Prez.</span>
                    </>
                }
                headerClassName='break-word'
                body={inPersonTemplate}
                align='center'
            />
            {renderHistoryColumn && (
                <Column
                    field='edit_history'
                    body={editHistoryTemplate}
                    align='center'
                    className='table-cell-border-left'
                />
            )}
        </DataTable>
    );
};
